import { PALETA_CATEGORICA } from '../servicios/chart-colors.service';
import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  input,
  output,
  signal,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import HighchartsHeatmap from 'highcharts/modules/heatmap';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsFullScreen from 'highcharts/modules/full-screen';
import { HighchartsChartModule } from 'highcharts-angular';

// Inicializar módulos. exporting + full-screen habilitan el botón "Ver en
// pantalla completa" (ambos vienen en el paquete highcharts, sin dep nueva).
HighchartsHeatmap(Highcharts);
HighchartsExporting(Highcharts);
HighchartsFullScreen(Highcharts);

// Configuración global de Highcharts
Highcharts.setOptions({
  colors: PALETA_CATEGORICA,
  // Porciones separadas por el color del fondo, no por blanco fijo
  plotOptions: {
    pie: { borderColor: 'var(--chart-background-color, #ffffff)' },
  },
  lang: {
    decimalPoint: ',',
    thousandsSep: '.',
    months: [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ],
    weekdays: [
      'Domingo',
      'Lunes',
      'Martes',
      'Miércoles',
      'Jueves',
      'Viernes',
      'Sábado',
    ],
    shortMonths: [
      'Ene',
      'Feb',
      'Mar',
      'Abr',
      'May',
      'Jun',
      'Jul',
      'Ago',
      'Sep',
      'Oct',
      'Nov',
      'Dic',
    ],
    shortWeekdays: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    downloadPNG: 'Descargar imagen PNG',
    downloadJPEG: 'Descargar imagen JPEG',
    downloadPDF: 'Descargar documento PDF',
    downloadSVG: 'Descargar imagen SVG',
    printChart: 'Imprimir gráfico',
    viewFullscreen: 'Ver en pantalla completa',
    exitFullscreen: 'Salir de pantalla completa',
  },
  credits: {
    enabled: false, // Ocultar créditos de Highcharts
  },
});

/**
 * Interfaz para datos de gráfico de tortas/pie
 */
export interface IPieChartData {
  name: string;
  y: number;
  sliced?: boolean;
  selected?: boolean;
  color?: string;
}

/**
 * Interfaz para datos de gráfico de barras/columnas
 */
export interface IBarChartData {
  name: string;
  data: number[];
  color?: string; // Color de la serie
  colors?: string[]; // Colores individuales de puntos (usa color de la serie primero)
}

/**
 * Interfaz para datos de gráfico de líneas/área
 */
export interface ILineChartData {
  name: string;
  data: number[] | [number, number][]; // Acepta array simple o pares [x, y]
  color?: string; // Color de la línea
}

/**
 * Interfaz para datos de gráfico de heatmap
 * Cada punto es [x, y, value] donde x e y son índices de categorías
 */
export interface IHeatmapChartData {
  data: [number, number, number][]; // [xIndex, yIndex, value]
  name?: string;
}

/**
 * Interfaz para datos de gráfico scatter
 * Cada punto es {x: timestamp_ms, y: rssi, z: toa}
 */
export interface IScatterChartData {
  name: string;
  data: { x: number; y: number; z: number }[]; // {x: timestamp_ms, y: rssi, z: toa}
  color?: string;
}

/**
 * Tipos de gráficos soportados
 */
export type TipoGrafico =
  | 'pie'
  | 'donut'
  | 'bar'
  | 'line'
  | 'column'
  | 'area'
  | 'heatmap'
  | 'scatter';

/**
 * Posición de la leyenda
 */
export type PosicionLeyenda = 'right' | 'bottom' | 'left' | 'top';

@Component({
  selector: 'app-graficos',
  imports: [HighchartsChartModule],
  templateUrl: './graficos.component.html',
  styleUrl: './graficos.component.scss',
})
export class GraficosComponent implements OnInit, OnChanges {
  public Highcharts: typeof Highcharts = Highcharts;
  public chartOptions = signal<Highcharts.Options | undefined>(undefined);
  public updateFlag = signal<boolean>(false);
  public chart = signal<Highcharts.Chart | undefined>(undefined);

  // Inputs
  tipo = input<TipoGrafico>('pie');
  datos = input<IPieChartData[]>([]); // Para pie/donut
  series = input<IBarChartData[] | ILineChartData[]>([]); // Para bar/column/line/area
  datosHeatmap = input<IHeatmapChartData>(); // Para heatmap
  datosScatter = input<IScatterChartData[]>([]); // Para scatter
  categorias = input<string[]>([]); // Categorías del eje X
  categoriasY = input<string[]>([]); // Categorías del eje Y (para heatmap)
  titulo = input<string>();
  subtitulo = input<string>();
  altura = input<string>('400px');
  mostrarLeyenda = input<boolean>(true);
  posicionLeyenda = input<PosicionLeyenda>('bottom'); // Nueva opción
  mostrarEtiquetas = input<boolean>(true);
  ocultarLabelsEjes = input<boolean>(false); // Ocultar labels de los ejes
  formatoEtiquetas = input<string>('{point.name}: {point.percentage:.1f}%');
  permitirSeleccion = input<boolean>(true);
  colorPaleta = input<string[]>(); // Paleta de colores personalizada
  innerSize = input<string>('0%'); // Para gráficos donut (ej: '50%')
  tituloEjeX = input<string>(); // Título del eje X
  tituloEjeY = input<string>(); // Título del eje Y
  suavizado = input<boolean>(false); // Suavizar líneas (spline)
  mostrarPuntos = input<boolean>(true); // Mostrar puntos en las líneas
  permitirDecimales = input<boolean>(true); // Permitir valores decimales en tooltips y ejes
  opciones = input<Partial<Highcharts.Options>>(); // Opciones adicionales/override
  pantallaCompleta = input<boolean>(false); // Muestra botón "Ver en pantalla completa"

  // Inputs específicos para scatter
  minEjeY = input<number>(); // Min del eje Y (para RSSI: -130)
  maxEjeY = input<number>(); // Max del eje Y (para RSSI: -70)
  colorAxisMin = input<number>(); // Min para escala de color
  colorAxisMax = input<number>(); // Max para escala de color
  colorAxisStops = input<[number, string][]>(); // Stops para gradiente

  // Outputs
  chartInstance = output<Highcharts.Chart>();
  puntoClick = output<IPieChartData>();

  constructor() {
    this.chartCallback = this.chartCallback.bind(this);
  }

  ngOnInit(): void {
    this.generarGrafico();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['datos'] ||
      changes['tipo'] ||
      changes['series'] ||
      changes['categorias'] ||
      changes['datosHeatmap'] ||
      changes['categoriasY'] ||
      changes['datosScatter']
    ) {
      this.generarGrafico();
    }
  }

  private generarGrafico(): void {
    if (this.tipo() === 'pie' || this.tipo() === 'donut') {
      this.generarGraficoPie();
    } else if (this.tipo() === 'bar' || this.tipo() === 'column') {
      this.generarGraficoBarras();
    } else if (this.tipo() === 'line' || this.tipo() === 'area') {
      this.generarGraficoLinea();
    } else if (this.tipo() === 'heatmap') {
      this.generarGraficoHeatmap();
    } else if (this.tipo() === 'scatter') {
      this.generarGraficoScatter();
    }
    this.updateFlag.set(true);
  }

  // Config de exporting: si `pantallaCompleta`, muestra el botón de menú SOLO con
  // "Ver en pantalla completa" (sin descargas). Si no, oculto.
  private exportingCfg(): Highcharts.ExportingOptions {
    if (!this.pantallaCompleta()) return { enabled: false };
    return {
      enabled: true,
      buttons: {
        contextButton: {
          menuItems: ['viewFullscreen'],
        },
      },
    };
  }

  // Labels de eje X para categorías con muchas franjas (fechas): autorrotan y
  // usan tipografía chica para no encimarse en gráficos angostos.
  private xLabelsCfg(): Highcharts.XAxisLabelsOptions {
    return {
      enabled: !this.ocultarLabelsEjes(),
      style: { color: 'var(--fg-grafico)', fontSize: '10px' },
      autoRotation: [0, -20, -45, -90],
    };
  }

  private obtenerConfigLeyenda(): Highcharts.LegendOptions {
    const posLeyenda = this.posicionLeyenda();

    switch (posLeyenda) {
      case 'right':
        return {
          enabled: this.mostrarLeyenda(),
          layout: 'vertical',
          align: 'right',
          verticalAlign: 'middle',
          itemStyle: { color: 'var(--p-text-color)' },
        };
      case 'left':
        return {
          enabled: this.mostrarLeyenda(),
          layout: 'vertical',
          align: 'left',
          verticalAlign: 'middle',
          itemStyle: { color: 'var(--p-text-color)' },
        };
      case 'top':
        return {
          enabled: this.mostrarLeyenda(),
          layout: 'horizontal',
          align: 'center',
          verticalAlign: 'top',
          itemStyle: { color: 'var(--p-text-color)' },
        };
      case 'bottom':
      default:
        return {
          enabled: this.mostrarLeyenda(),
          layout: 'horizontal',
          align: 'center',
          verticalAlign: 'bottom',
          itemStyle: { color: 'var(--p-text-color)' },
        };
    }
  }

  private generarGraficoPie(): void {
    const innerSize = this.tipo() === 'donut' ? '50%' : this.innerSize();

    this.chartOptions.set({
      chart: {
        type: 'pie',
        height: this.altura(),
        backgroundColor: 'var(--chart-background-color, #ffffff)',
        style: {
          color: 'var(--fg-grafico)',
        },
      },
      title: {
        text: this.titulo(),
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: 'var(--fg-grafico)',
        },
      },
      subtitle: {
        text: this.subtitulo(),
        style: {
          color: 'var(--fg-grafico)',
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: this.permitirSeleccion(),
          cursor: this.permitirSeleccion() ? 'pointer' : 'default',
          dataLabels: {
            enabled: this.mostrarEtiquetas(),
            format: this.formatoEtiquetas(),
            style: {
              fontSize: '12px',
              color: 'var(--fg-grafico)',
            },
          },
          showInLegend: this.mostrarLeyenda(),
          innerSize: innerSize,
          point: {
            events: {
              click: (event: any) => {
                const punto = event.point.options as IPieChartData;
                this.puntoClick.emit(punto);
              },
            },
          },
        },
      },
      legend: this.obtenerConfigLeyenda(),
      series: [
        {
          name: this.titulo() || 'Datos',
          type: 'pie',
          data: this.datos(),
          colors: this.colorPaleta(),
        },
      ],
      tooltip: {
        headerFormat: '<span style="font-size:11px">{series.name}</span><br>',
        pointFormat:
          '<span style="color:{point.color}">{point.name}</span>: <b>{point.y}</b> ({point.percentage:.1f}%)<br/>',
        borderRadius: 4,
        shadow: false,
      },
      exporting: {
        enabled: false,
      },
      ...this.opciones(), // Permite override de opciones
    });
  }

  private generarGraficoBarras(): void {
    const paletaDefault = PALETA_CATEGORICA;

    this.chartOptions?.set({
      chart: {
        type: this.tipo() as 'bar' | 'column',
        height: this.altura(),
        backgroundColor: 'var(--chart-background-color, #ffffff)',
        style: { color: 'var(--fg-grafico)' },
        // Sin animación de crecimiento: un redraw durante la animación dejaba la
        // serie en scale(1 ~0) → columnas de altura casi nula (invisibles).
        animation: false,
      },
      title: {
        text: this.titulo(),
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: 'var(--fg-grafico)',
        },
      },
      subtitle: {
        text: this.subtitulo(),
        style: { color: 'var(--fg-grafico)' },
      },
      xAxis: {
        categories: this.categorias(),
        title: {
          text: this.tituloEjeX(),
          style: { color: 'var(--fg-grafico)' },
        },
        labels: this.xLabelsCfg(),
      },
      yAxis: {
        title: {
          text: this.tituloEjeY(),
          style: { color: 'var(--fg-grafico)' },
        },
        labels: {
          enabled: !this.ocultarLabelsEjes(),
          style: { color: 'var(--fg-grafico)' },
          format: this.permitirDecimales() ? '{value}' : '{value:.0f}',
        },
        allowDecimals: this.permitirDecimales(),
      },
      legend: this.obtenerConfigLeyenda(),
      plotOptions: {
        series: {
          animation: false,
          dataLabels: {
            enabled: this.mostrarEtiquetas(),
            style: {
              fontSize: '11px',
              fontWeight: 'normal',
              color: 'var(--fg-grafico)',
              textOutline: 'none',
            },
          },
        },
        bar: {
          colorByPoint: this.colorPaleta() && this.series()?.length === 1,
        },
        column: {
          colorByPoint: this.colorPaleta() && this.series()?.length === 1,
        },
      },
      series: this.series()?.map((s) => {
        const barData = s as any; // Type assertion para acceder a color y colors
        const colors = barData?.colors || this.colorPaleta() || paletaDefault;
        // Solo usar colorByPoint si hay colores por punto definidos o si hay colorPaleta con una sola serie
        const useColorByPoint =
          barData.colors || (this.colorPaleta() && this.series().length === 1);

        return {
          name: s?.name,
          type: this.tipo() as 'bar' | 'column',
          data: useColorByPoint
            ? s.data.map((val, idx) => ({
                y: typeof val === 'number' ? val : val[1], // Manejar [x,y] o número
                name: this.categorias()?.[idx] ?? '',
                color: colors[idx % colors?.length],
              }))
            : s.data,
          color: barData?.color, // Usar color de la serie si está disponible
          colorByPoint: useColorByPoint,
          ...(useColorByPoint ? { colors } : {}),
        } as any;
      }),
      tooltip: {
        headerFormat:
          this.series().length > 1
            ? '<span style="font-size:11px">{series.name}</span><br>'
            : '',
        pointFormat: this.permitirDecimales()
          ? '<b>{point.y}</b><br/>'
          : '<b>{point.y:.0f}</b><br/>',
        borderRadius: 4,
        shadow: false,
      },
      exporting: this.exportingCfg(),
      // NUNCA `colors: []` ni `colors: undefined`: el primero deja las barras sin
      // relleno y el segundo pisa la paleta por defecto, y con `colorByPoint`
      // Highcharts revienta en `resolveColor`. Sin paleta propia, no se manda.
      ...(this.colorPaleta()?.length ? { colors: this.colorPaleta() } : {}),
      ...this.opciones(),
    });
  }

  private generarGraficoLinea(): void {
    // Determinar si usar spline (suavizado) o line
    const chartType = this.suavizado()
      ? this.tipo() === 'area'
        ? 'areaspline'
        : 'spline'
      : (this.tipo() as 'line' | 'area');

    this.chartOptions.set({
      chart: {
        type: chartType,
        height: this.altura(),
        backgroundColor: 'var(--chart-background-color, #ffffff)',
        style: { color: 'var(--fg-grafico)' },
      },
      title: {
        text: this.titulo(),
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: 'var(--fg-grafico)',
        },
      },
      subtitle: {
        text: this.subtitulo(),
        style: { color: 'var(--fg-grafico)' },
      },
      xAxis: {
        categories: this.categorias(),
        title: {
          text: this.tituloEjeX(),
          style: { color: 'var(--fg-grafico)' },
        },
        labels: this.xLabelsCfg(),
      },
      yAxis: {
        title: {
          text: this.tituloEjeY(),
          style: { color: 'var(--fg-grafico)' },
        },
        labels: {
          style: { color: 'var(--fg-grafico)' },
          format: this.permitirDecimales() ? '{value}' : '{value:.0f}',
        },
        allowDecimals: this.permitirDecimales(),
      },
      legend: this.obtenerConfigLeyenda(),
      plotOptions: {
        series: {
          dataLabels: {
            enabled: this.mostrarEtiquetas(),
            style: {
              fontSize: '11px',
              fontWeight: 'normal',
              color: 'var(--fg-grafico)',
              textOutline: 'none',
            },
          },
          marker: {
            enabled: this.mostrarPuntos(),
            radius: 4,
          },
        },
      },
      series: this.series().map((s) => {
        const lineData = s as ILineChartData;
        const color =
          lineData.color ||
          (this.colorPaleta() ? this.colorPaleta()![0] : undefined);

        return {
          name: s.name,
          type: chartType,
          data: s.data,
          color: color,
        } as any;
      }),
      tooltip: {
        headerFormat: '<span style="font-size:11px">{series.name}</span><br>',
        pointFormat: this.permitirDecimales()
          ? '<b>{point.y}</b><br/>'
          : '<b>{point.y:.0f}</b><br/>',
        borderRadius: 4,
        shadow: false,
      },
      exporting: this.exportingCfg(),
      ...(this.colorPaleta()?.length ? { colors: this.colorPaleta() } : {}),
      ...this.opciones(),
    });
  }

  private generarGraficoHeatmap(): void {
    const datos = this.datosHeatmap();
    if (!datos) return;

    this.chartOptions.set({
      chart: {
        type: 'heatmap',
        height: this.altura(),
        backgroundColor: 'var(--chart-background-color, #ffffff)',
        style: { color: 'var(--fg-grafico)' },
      },
      title: {
        text: this.titulo(),
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: 'var(--fg-grafico)',
        },
      },
      subtitle: {
        text: this.subtitulo(),
        style: { color: 'var(--fg-grafico)' },
      },
      xAxis: {
        categories: this.categorias(),
        title: {
          text: this.tituloEjeX(),
          style: { color: 'var(--fg-grafico)' },
        },
        labels: {
          style: { color: 'var(--fg-grafico)' },
          rotation: -45,
        },
      },
      yAxis: {
        categories: this.categoriasY(),
        title: {
          text: this.tituloEjeY(),
          style: { color: 'var(--fg-grafico)' },
        },
        labels: {
          // useHTML: permite categorías con 2 líneas (nombre + EUI chico).
          useHTML: true,
          style: { color: 'var(--fg-grafico)' },
        },
        reversed: false,
      },
      colorAxis: {
        min: 0,
        minColor: '#e6f3ff',
        maxColor: '#0066cc',
      },
      legend: {
        enabled: this.mostrarLeyenda(),
        align: 'right',
        layout: 'vertical',
        verticalAlign: 'middle',
      },
      series: [
        {
          type: 'heatmap',
          name: datos.name || 'Cantidad',
          data: datos.data,
          dataLabels: {
            enabled: this.mostrarEtiquetas(),
            color: '#000000',
            style: {
              fontSize: '10px',
              textOutline: 'none',
            },
          },
        },
      ],
      tooltip: {
        formatter: function () {
          const point = this.point as any;
          const strip = (s: unknown) =>
            String(s ?? '')
              .replace(/<[^>]*>/g, ' ')
              .replace(/\s+/g, ' ')
              .trim();
          const xCat = strip((this.series.xAxis as any).categories[point.x]);
          const yCat = strip((this.series.yAxis as any).categories[point.y]);
          return `<b>${xCat}</b><br/>${yCat}<br/>Cantidad: <b>${point.value}</b>`;
        },
      },
      exporting: this.exportingCfg(),
      ...this.opciones(),
    });
  }

  private generarGraficoScatter(): void {
    const datos = this.datosScatter();
    if (!datos || datos.length === 0) return;

    // Configuración de color axis para gradiente basado en ToA
    const colorAxisConfig: Highcharts.ColorAxisOptions = {
      min: this.colorAxisMin() ?? 0,
      max: this.colorAxisMax() ?? 1000,
      stops: this.colorAxisStops()?.length
        ? this.colorAxisStops()!
        : [
            [0, '#4CAF50'], // Verde - ToA bajo
            [0.3, '#8BC34A'], // Verde claro
            [0.5, '#FFC107'], // Amarillo
            [0.7, '#FF9800'], // Naranja
            [1, '#F44336'], // Rojo - ToA alto
          ],
    };

    this.chartOptions.set({
      chart: {
        type: 'scatter',
        height: this.altura(),
        backgroundColor: 'var(--chart-background-color, #ffffff)',
        style: { color: 'var(--fg-grafico)' },
        zooming: {
          type: 'xy',
        },
      } as any,
      time: {
        useUTC: false, // Usar hora local del navegador
      },
      title: {
        text: this.titulo(),
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: 'var(--fg-grafico)',
        },
      },
      subtitle: {
        text: this.subtitulo(),
        style: { color: 'var(--fg-grafico)' },
      },
      xAxis: {
        type: 'datetime',
        title: {
          text: this.tituloEjeX() || 'Tiempo',
          style: { color: 'var(--fg-grafico)' },
        },
        labels: {
          style: { color: 'var(--fg-grafico)' },
          format: '{value:%H:%M}',
        },
      },
      yAxis: {
        min: this.minEjeY() ?? -130,
        max: this.maxEjeY() ?? -70,
        title: {
          text: this.tituloEjeY() || 'RSSI (dBm)',
          style: { color: 'var(--fg-grafico)' },
        },
        labels: {
          style: { color: 'var(--fg-grafico)' },
          format: '{value}',
        },
        gridLineColor: 'var(--chart-grid-color)',
      },
      colorAxis: colorAxisConfig,
      legend: {
        enabled: this.mostrarLeyenda(),
        align: 'right',
        layout: 'vertical',
        verticalAlign: 'middle',
      },
      plotOptions: {
        scatter: {
          marker: {
            radius: 5,
            states: {
              hover: {
                enabled: true,
                lineColor: 'rgb(100,100,100)',
              },
            },
          },
        },
      },
      series: datos.map((s) => ({
        type: 'scatter',
        name: s.name,
        data: s.data,
        color: s.color,
      })) as any,
      tooltip: {
        headerFormat: '',
        borderRadius: 6,
        shadow: true,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        borderColor: '#333',
        borderWidth: 1,
        padding: 12,
        useHTML: true,
        // El formatter será personalizado vía opciones
      },
      exporting: { enabled: false },
      ...this.opciones(), // Permite override de tooltip y otras opciones
    });
  }

  public chartCallback: Highcharts.ChartCallbackFunction = (
    chart: Highcharts.Chart,
  ) => {
    this.chart.set(chart);
    this.chartInstance.emit(chart);
  };

  /**
   * Método público para exportar el gráfico
   */
  public exportarPNG(): void {
    this.chart()?.exportChart({ type: 'image/png' }, {});
  }

  public exportarJPEG(): void {
    this.chart()?.exportChart({ type: 'image/jpeg' }, {});
  }

  public exportarPDF(): void {
    this.chart()?.exportChart({ type: 'application/pdf' }, {});
  }

  public exportarSVG(): void {
    this.chart()?.exportChart({ type: 'image/svg+xml' }, {});
  }

  public imprimir(): void {
    this.chart()?.print();
  }
}
